import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Phone, MessageSquare } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import { stadtbezirke } from '../mock';

const ServiceTemplate = ({ 
  title, 
  slug, 
  metaTitle, 
  metaDescription,
  intro,
  whatIncluded,
  whenNeeded,
  pricing,
  valueCredit,
  disposal,
  icon: Icon
}) => {
  return (
    <div className="bg-white py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Breadcrumb items={[
          { label: 'Leistungen', href: '/leistungen' },
          { label: title }
        ]} />
        
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              {Icon && <Icon className="h-12 w-12 text-[#16A34A]" />}
              <h1 className="text-4xl font-bold text-[#0F172A]">{title}</h1>
            </div>
            
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">{intro}</p>

            {/* Faktenbox Dortmund */}
            <div className="bg-gray-50 rounded-lg p-6 mb-12">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">Faktenbox Dortmund</h2>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Einsatzgebiet: Alle 12 Stadtbezirke Dortmunds</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Auch bei schwierigen Zugangswegen (Altbau, Innenstadt-Parkzonen)</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Express-Termine innerhalb weniger Tage möglich</span>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {stadtbezirke.slice(0, 6).map((bezirk, index) => (
                  <span key={index} className="px-3 py-1 bg-white text-gray-700 rounded-full text-sm">
                    {bezirk}
                  </span>
                ))}
                <span className="px-3 py-1 bg-white text-gray-700 rounded-full text-sm">+ 6 weitere</span>
              </div>
            </div>

            {/* Was ist enthalten? */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Was ist enthalten?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">{whatIncluded}</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Komplette Räumung aller Gegenstände</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Besenreine Übergabe mit Kehrarbeiten</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Fachgerechte Entsorgung nach Vorschrift</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Entsorgungsnachweis auf Wunsch</span>
                </li>
              </ul>
            </section>

            {/* Wann lohnt sich's? */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Wann lohnt sich diese Leistung?</h2>
              <p className="text-gray-700 leading-relaxed">{whenNeeded}</p>
            </section>

            {/* Preise & Dauer */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Preise & Dauer</h2>
              <p className="text-gray-700 leading-relaxed mb-4">{pricing}</p>
              <div className="bg-[#16A34A]/5 border-l-4 border-[#16A34A] p-4 rounded">
                <p className="text-sm text-gray-700">
                  <strong>Hinweis:</strong> Nach Besichtigung oder Foto-/Videozusendung erhalten Sie einen 
                  verbindlichen Festpreis. Keine versteckten Kosten.
                </p>
              </div>
            </section>

            {/* Wertanrechnung */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Wertanrechnung</h2>
              <p className="text-gray-700 leading-relaxed">{valueCredit}</p>
            </section>

            {/* Entsorgung */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Schonend & gesetzeskonform entsorgen</h2>
              <p className="text-gray-700 leading-relaxed">{disposal}</p>
            </section>

            {/* Image Section */}
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <img
                src="https://images.unsplash.com/photo-1630699144919-681cf308ae82"
                alt={`${title} - Besenreines Ergebnis`}
                className="rounded-lg shadow-md w-full h-64 object-cover"
              />
              <img
                src="https://images.pexels.com/photos/3434533/pexels-photo-3434533.jpeg"
                alt={`${title} - Professionelle Durchführung`}
                className="rounded-lg shadow-md w-full h-64 object-cover"
              />
            </div>

            {/* Internal Links */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild className="bg-[#16A34A] hover:bg-[#16A34A]/90 text-white">
                <Link to="/kontakt">Jetzt Angebot anfordern</Link>
              </Button>
              <Button asChild variant="outline" className="border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white">
                <Link to="/preise">Alle Preise ansehen</Link>
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 bg-gray-50">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-[#0F172A] mb-4">Schnellkontakt</h3>
                <div className="space-y-4">
                  <Button
                    asChild
                    className="w-full bg-[#16A34A] hover:bg-[#16A34A]/90 text-white"
                  >
                    <a href="tel:+4923139085116" className="flex items-center justify-center gap-2">
                      <Phone className="h-5 w-5" />
                      0231 39085116
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white"
                  >
                    <a 
                      href="https://wa.me/4923139085116" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2"
                    >
                      <MessageSquare className="h-5 w-5" />
                      WhatsApp
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full"
                  >
                    <Link to="/kontakt">Zum Formular</Link>
                  </Button>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="font-semibold text-[#0F172A] mb-3">Weitere Leistungen</h4>
                  <ul className="space-y-2 text-sm">
                    <li>
                      <Link to="/haushaltsaufloesung-dortmund" className="text-[#16A34A] hover:underline">
                        Haushaltsauflösung
                      </Link>
                    </li>
                    <li>
                      <Link to="/wohnungsaufloesung-dortmund" className="text-[#16A34A] hover:underline">
                        Wohnungsauflösung
                      </Link>
                    </li>
                    <li>
                      <Link to="/messie-wohnung-dortmund" className="text-[#16A34A] hover:underline">
                        Messi-Wohnung Räumung
                      </Link>
                    </li>
                    <li>
                      <Link to="/keller-dachboden-entruempelung-dortmund" className="text-[#16A34A] hover:underline">
                        Keller & Dachboden
                      </Link>
                    </li>
                    <li>
                      <Link to="/sperrmuell-abtransport-dortmund" className="text-[#16A34A] hover:underline">
                        Sperrmüll-Abtransport
                      </Link>
                    </li>
                    <li>
                      <Link to="/nachlassaufloesung-dortmund" className="text-[#16A34A] hover:underline">
                        Nachlassauflösung
                      </Link>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceTemplate;
